/* @flow */
import * as React from "react";
import Theme from "../Theme";
import { HANDLE_SIZE } from "./Handle";

export function clampTooltipOffset({
  tooltipWidth,
  sliderWidth: maybeWidth, // default props/arguments won't work if this is null.
  ratio,
}: {
  tooltipWidth: number,
  sliderWidth: ?number,
  ratio: number,
}): number {
  const sliderWidth = maybeWidth || 0;
  const tooltipCenter = sliderWidth * ratio + HANDLE_SIZE / 2;
  const totalWidth = sliderWidth + HANDLE_SIZE;
  const halfTooltipWidth = tooltipWidth / 2;
  let offset = 0;
  if (tooltipCenter + -halfTooltipWidth < 0) {
    // tooltip is too close to the left end, offset further to the right
    offset = halfTooltipWidth - tooltipCenter;
  }
  if (tooltipCenter + halfTooltipWidth > totalWidth) {
    // tooltip is too close to the right end, offset further to the left
    offset = totalWidth - (tooltipCenter + halfTooltipWidth);
  }
  return offset;
}

type Props = {
  isPersistentValue: boolean,
  isVisible: boolean,
  label: string,
  ratio: number,
  sliderWidth: ?number,
  style: ?Object,
};

type State = {
  tooltipWidth: number,
};

export default class SliderTooltip extends React.Component<Props, State> {
  static defaultProps = {
    isPersistentValue: false,
  };

  state = {
    tooltipWidth: 30,
  };

  _tooltipEl: ?HTMLElement;

  componentDidMount() {
    this._syncOffset();
  }

  componentDidUpdate() {
    this._syncOffset();
  }

  _syncOffset() {
    if (!this._tooltipEl) {
      return;
    }
    // If the width changes the clampingOffset can become out of sync with the real offsetWidth.
    // This will frequently double-render, but the renders are isolated to this component
    if (this.state.tooltipWidth === this._tooltipEl.offsetWidth) {
      return;
    }
    this.setState({ tooltipWidth: this._tooltipEl.offsetWidth });
  }

  render() {
    const {
      isPersistentValue,
      isVisible,
      label,
      ratio,
      sliderWidth,
      style,
    } = this.props;

    const { tooltipWidth } = this.state;

    const TOOLTIP_SPACING = isPersistentValue
      ? HANDLE_SIZE / 2
      : HANDLE_SIZE / 2 + 8;
    const TAIL_HEIGHT = 6;

    const clampingOffset = clampTooltipOffset({
      tooltipWidth,
      sliderWidth,
      ratio,
    });

    let styles = {
      offsetPositioning: {
        position: "relative",
        left: "50%",
        top: "50%",
        opacity: isVisible ? 1 : 0,
        transform: `scale(${isVisible ? 1 : 0})`,
        transition: `all 200ms ${isVisible ? "ease-out" : "ease-in"}`,
        width: 0,
      },
      tooltip: {
        position: "absolute",
        display: "inline",
        zIndex: Theme.zIndexLayers.POPUP,
        whiteSpace: "pre",
        transform: `translate(calc(-50% + ${clampingOffset}px), calc(-98% + ${-TOOLTIP_SPACING}px))`, // 98% so there is no line between the tooltip and tail on retina displays
        ...style,
      },
      tail: {
        position: "absolute",
        transformOrigin: "0 0",
        border: `solid ${TAIL_HEIGHT}px transparent`,
        zIndex: Theme.zIndexLayers.POPUP,
        ...(style && { borderTopColor: style.backgroundColor }),
        transform: `translate(-50%, ${-TOOLTIP_SPACING}px)`,
      },
    };

    return (
      <div data-component-name="SliderTooltip" style={styles.offsetPositioning}>
        <div
          ref={(el) => {
            this._tooltipEl = el;
          }}
          style={styles.tooltip}
        >
          {label}
        </div>
        {!isPersistentValue ? <div style={styles.tail} /> : null}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ../nx-components/src/Slider/SliderTooltip.jsx